import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fileUrl, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { AdminHead, FilterSelect } from "@/components/admin/AdminUI";
import { PageLoader } from "@/components/layout/RequireAuth";

const Doc = ({ path, label, testId }) => path ? (
  <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="block" data-testid={testId}>
    <p className="mb-1 text-xs text-stone-500">{label}</p>
    <img src={fileUrl(path)} alt={label} className="h-44 w-full rounded-xl border border-stone-200 bg-stone-50 object-contain" />
  </a>
) : null;

const Item = ({ p, onDone }) => {
  const [reason, setReason] = useState("");
  const decide = async (approve) => {
    try {
      await api.post(`/admin/verifications/${p.user_id}`, { approve, reason });
      toast.success(approve ? "Penyedia disetujui" : "Pengajuan ditolak");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const v = p.verification;
  return (
    <div className="card-soft space-y-4 p-5" data-testid={`verification-item-${p.user_id}`}>
      <div>
        <p className="font-bold text-stone-900">{p.business_name}</p>
        <p className="text-xs text-stone-500">Diajukan {formatDate(v.submitted_at)} · Status: {v.status}{v.reason ? ` · Alasan: ${v.reason}` : ""}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Doc path={v.ktp_path} label="Foto KTP" testId={`verification-ktp-${p.user_id}`} />
        <Doc path={v.doc_path} label="Dokumen usaha" testId={`verification-doc-${p.user_id}`} />
      </div>
      {v.status === "menunggu" && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={() => decide(true)} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid={`approve-${p.user_id}`}>Setujui</Button>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan penolakan" className="rounded-xl" data-testid={`reject-reason-${p.user_id}`} />
          <Button variant="outline" disabled={!reason.trim()} onClick={() => decide(false)} className="rounded-xl text-rose-700" data-testid={`reject-${p.user_id}`}>Tolak</Button>
        </div>
      )}
    </div>
  );
};

export default function AdminVerification() {
  const [status, setStatus] = useState("menunggu");
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState(null);
  const load = () => api.get("/admin/verifications", { params: { status } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  const toggle = async (v) => {
    try {
      const { data } = await api.put("/admin/settings", { ...settings, require_provider_verification: v });
      setSettings(data);
      toast.success(v ? "Verifikasi wajib diaktifkan" : "Verifikasi wajib dinonaktifkan");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-verification">
      <AdminHead title="Verifikasi Penyedia" desc="Dokumen bersifat privat: hanya penyedia pemilik dan admin yang dapat membukanya." />
      {settings && (
        <label className="card-soft mb-6 flex items-center justify-between gap-4 p-5">
          <span>
            <span className="block text-sm font-bold text-stone-900">Wajibkan verifikasi penyedia</span>
            <span className="text-xs text-stone-500">Jika aktif, penyedia belum terverifikasi disembunyikan dari publik, tidak bisa mengirim penawaran, dan paketnya tidak bisa dipesan.</span>
          </span>
          <Switch checked={settings.require_provider_verification} onCheckedChange={toggle} data-testid="require-verification-switch" />
        </label>
      )}
      <div className="mb-4"><FilterSelect value={status} onChange={setStatus} testId="verification-status-filter" options={[["menunggu", "Menunggu"], ["ditolak", "Ditolak"], ["terverifikasi", "Terverifikasi"]]} /></div>
      {items === null ? <PageLoader /> : items.length === 0 ? <p className="text-sm text-stone-500" data-testid="verification-empty">Tidak ada pengajuan.</p> : (
        <div className="grid gap-4 lg:grid-cols-2">{items.map((p) => <Item key={p.user_id} p={p} onDone={load} />)}</div>
      )}
    </div>
  );
}
